import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { creditReportService } from '../../services/creditReportService';
import { PENDING_REFERENCE_KEY } from './CreditReport';

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24; // ~2 minutes; delivery can lag behind payment

export default function PaymentComplete() {
  const [params] = useSearchParams();
  const [reference] = useState(() => {
    // Paystack appends its own reference; only trust it when it's the RAAS+ one we started.
    const fromUrl = [params.get('reference'), params.get('trxref')].find(r => r?.startsWith('raasplus_'));
    if (fromUrl) return fromUrl;
    try { return sessionStorage.getItem(PENDING_REFERENCE_KEY); } catch { return null; }
  });
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!reference) return undefined;
    let polls = 0;
    let timer = null;
    let cancelled = false;

    const check = async () => {
      polls += 1;
      try {
        const { data } = await creditReportService.status(reference);
        if (cancelled) return;
        setOrder(data.data);
        if (data.data.status !== 'pending') {
          try { sessionStorage.removeItem(PENDING_REFERENCE_KEY); } catch { /* storage unavailable */ }
          return;
        }
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) { setError('We could not find this order.'); return; }
      }
      if (polls >= MAX_POLLS) setGaveUp(true);
      else timer = setTimeout(check, POLL_INTERVAL_MS);
    };
    check();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [reference]);

  let icon = '⏳', title = 'Confirming your payment…', body = 'This usually takes a few seconds. Please keep this page open.';
  if (!reference || error) {
    icon = '⚠'; title = 'No order to show'; body = error || "We couldn't find a credit report order for this payment.";
  } else if (order?.status === 'fulfilled') {
    icon = '✓'; title = 'Payment received!';
    body = order.is_subscription
      ? `Your ${order.report_name || 'subscription'} is active. A confirmation with your start and end dates has been emailed to you.`
      : `Your ${order.report_name || 'credit report'} has been emailed to you. Check your inbox (and spam folder) shortly.`;
  } else if (order?.status === 'failed') {
    icon = '⚠'; title = 'Payment was not completed'; body = order.message || 'Your payment did not go through. You have not been charged for a report.';
  } else if (gaveUp) {
    icon = '✉'; title = 'Payment is processing';
    body = "If your payment went through, your report will arrive by email shortly. You can check its status on the Credit Report page.";
  }

  return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <div className="card" style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
        <div style={{ fontSize: '2.25rem', marginBottom: '0.75rem' }}>{icon}</div>
        <h1 style={{ fontSize: '1.35rem', fontWeight: 700, marginBottom: '0.5rem' }}>{title}</h1>
        <p style={{ color: 'var(--text-2)', fontSize: '0.92rem', marginBottom: '1.75rem' }}>{body}</p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/credit-report" className="btn btn-primary">Credit Report</Link>
          <Link to="/dashboard" className="btn btn-ghost">Dashboard</Link>
        </div>
      </div>
    </div>
  );
}
